// ============================================================================
// FSA TEMPLATE UPGRADE MODAL (ADR-0004 Phase 2)
// ============================================================================
// Auditor-initiated move of ONE engagement to a newer template version.
// Opens straight into the code-mapped preview diff (schedules/classes added
// or removed, notes whose saved data would orphan, TB rows that would become
// unclassified), then applies transactionally after an explicit confirm.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { fsaTemplateUpgradeService } from 'api/services/fsa/fsaTemplateUpgradeService';
import type { ID } from 'api/types';
import type { TemplateUpgradeItem, TemplateUpgradePreview } from 'api/types/fsa/templateUpgrade.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  templateName?: string | null;
  onClose: () => void;
  /** Fired after a successful upgrade — parent refetches settings/TB/notes. */
  onUpgraded: () => void;
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8rem', fontWeight: 600, margin: '10px 0 4px', color: 'var(--text-primary)',
  display: 'flex', alignItems: 'center', gap: 6,
};

const chipListStyle: React.CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 110, overflowY: 'auto',
};

const chipStyle = (bg: string, fg: string): React.CSSProperties => ({
  background: bg, color: fg, borderRadius: 10, padding: '2px 8px',
  fontSize: '0.72rem', fontWeight: 500, whiteSpace: 'nowrap',
});

const ChipSection: React.FC<{ title: string; items: TemplateUpgradeItem[]; bg: string; fg: string }> =
  ({ title, items, bg, fg }) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div style={sectionTitleStyle}>{title} <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>({items.length})</span></div>
        <div style={chipListStyle}>
          {items.map(i => (
            <span key={i.code} style={chipStyle(bg, fg)} title={i.name ?? i.code}>{i.code}</span>
          ))}
        </div>
      </div>
    );
  };

const FsaTemplateUpgradeModal: React.FC<Props> = ({ isOpen, engagementId, templateName, onClose, onUpgraded }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<TemplateUpgradePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setPreview(null);
    setError(null);
    setLoading(true);
    fsaTemplateUpgradeService.preview(engagementId)
      .then(setPreview)
      .catch((err: any) => setError(err?.response?.data?.message || err?.message || 'Could not load the upgrade preview.'))
      .finally(() => setLoading(false));
  }, [isOpen, engagementId]);

  const handleConfirmUpgrade = useCallback(async () => {
    if (!preview) return;
    setUpgrading(true);
    try {
      const result = await fsaTemplateUpgradeService.upgrade(engagementId, preview.toVersion);
      setUpgrading(false);
      setConfirmOpen(false);
      notificationService.toast.success(
        `Upgraded to ${result.templateCode} v${result.toVersion} — ${result.notesRekeyed} notes and ${result.tbEntriesRekeyed} TB rows carried over.`,
      );
      onUpgraded();
      onClose();
    } catch (err: any) {
      setUpgrading(false);
      setConfirmOpen(false);
      const data = err?.response?.data;
      notificationService.toast.error(
        data?.message
          ?? (Array.isArray(data?.errors) && data.errors.length ? data.errors.join('; ') : null)
          ?? err?.message ?? 'Template upgrade failed.',
      );
    }
  }, [engagementId, preview, onUpgraded, onClose]);

  if (!isOpen) return null;

  const canUpgrade = !!preview && !preview.blocked && !preview.alreadyOnTarget && !loading && !upgrading;
  const hasStructuralChanges = !!preview && (
    preview.schedulesAdded.length + preview.schedulesRemoved.length +
    preview.classesAdded.length + preview.classesRemoved.length > 0);
  const hasWarnings = !!preview && (preview.orphanedNotes.length > 0 || preview.tbEntriesToUnclassify > 0);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Upgrade Template</h2>
          <button className="modal-close" onClick={onClose} disabled={upgrading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {loading && (
            <div style={{ padding: '1.5rem 0', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
              Comparing template versions…
            </div>
          )}

          {error && (
            <div style={{ padding: '0.75rem', borderRadius: 6, background: '#fee2e2', color: '#991b1b', fontSize: '0.82rem' }}>
              {error}
            </div>
          )}

          {preview && !loading && (
            <>
              {/* Version banner */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '0.6rem 0.75rem',
                borderRadius: 6, background: 'var(--bg-secondary)', marginBottom: 4,
              }}>
                <strong style={{ fontSize: '0.85rem' }}>{templateName || preview.templateCode}</strong>
                <span style={chipStyle('#e0e7ff', '#3730a3')}>v{preview.fromVersion}</span>
                <IconSystem.arrowRight size={14} />
                <span style={chipStyle('#dcfce7', '#166534')}>v{preview.toVersion}</span>
              </div>

              {preview.alreadyOnTarget && (
                <div style={{ padding: '0.75rem', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: '0.82rem' }}>
                  This engagement is already on the latest template version — nothing to upgrade.
                </div>
              )}

              {preview.blocked && (
                <div style={{ padding: '0.75rem', borderRadius: 6, background: '#fee2e2', color: '#991b1b', fontSize: '0.82rem' }}>
                  {preview.blockReason ?? 'This engagement cannot be upgraded.'}
                </div>
              )}

              {!preview.alreadyOnTarget && !preview.blocked && (
                <>
                  {!hasStructuralChanges && (
                    <div style={{ padding: '0.6rem 0.75rem', borderRadius: 6, background: '#dcfce7', color: '#166534', fontSize: '0.8rem' }}>
                      No schedules or classes are added or removed — all saved data carries over 1:1.
                    </div>
                  )}

                  <ChipSection title="Notes / schedules added" items={preview.schedulesAdded} bg="#dcfce7" fg="#166534" />
                  <ChipSection title="Notes / schedules removed" items={preview.schedulesRemoved} bg="#fee2e2" fg="#991b1b" />
                  <ChipSection title="Classes added" items={preview.classesAdded} bg="#dcfce7" fg="#166534" />
                  <ChipSection title="Classes removed" items={preview.classesRemoved} bg="#fee2e2" fg="#991b1b" />

                  {hasWarnings && (
                    <div style={{
                      marginTop: 10, padding: '0.6rem 0.75rem', borderRadius: 6,
                      background: '#fef3c7', color: '#92400e', fontSize: '0.8rem',
                      display: 'flex', flexDirection: 'column', gap: 4,
                    }}>
                      <strong style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconSystem.alert size={14} /> Review before upgrading
                      </strong>
                      {preview.orphanedNotes.length > 0 && (
                        <span>
                          {preview.orphanedNotes.length} note{preview.orphanedNotes.length === 1 ? '' : 's'} with saved data no longer exist in the new version
                          ({preview.orphanedNotes.map(n => n.code).join(', ')}) — the data is kept but will no longer appear.
                        </span>
                      )}
                      {preview.tbEntriesToUnclassify > 0 && (
                        <span>
                          {preview.tbEntriesToUnclassify} trial-balance ledger row{preview.tbEntriesToUnclassify === 1 ? '' : 's'} will become
                          UNCLASSIFIED (their class was removed) and re-appear in the mapping queue.
                        </span>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    All amounts, workspaces and note numbering carry over unchanged — matching is by note / class code.
                    The engagement stays pinned to v{preview.toVersion} after the upgrade. This action cannot be undone from the UI.
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={upgrading}>Cancel</button>
          <button className="btn btn-primary" onClick={() => setConfirmOpen(true)} disabled={!canUpgrade}>
            {upgrading ? 'Upgrading…' : preview ? `Upgrade to v${preview.toVersion}` : 'Upgrade'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Upgrade Template Version"
        message={preview
          ? `Move this engagement from ${preview.templateCode} v${preview.fromVersion} to v${preview.toVersion}? ` +
            (hasWarnings
              ? 'Some saved data will be orphaned / unclassified as shown in the preview.'
              : 'All saved data carries over by code.')
          : ''}
        confirmLabel="Upgrade"
        cancelLabel="Cancel"
        variant={hasWarnings ? 'danger' : 'default'}
        loading={upgrading}
        loadingMessage="Upgrading…"
        onConfirm={handleConfirmUpgrade}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default FsaTemplateUpgradeModal;
